import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import type { PlatformImmeuble } from "@/store/platform/types";
import type { Immeuble, ImmeubleInput } from "./types";

interface ImmeublesContextValue {
  immeubles: Immeuble[];
  addImmeuble: (input: ImmeubleInput) => Immeuble;
  getImmeuble: (id: string) => Immeuble | undefined;
  removeImmeuble: (id: string) => void;
  updateImmeuble: (id: string, input: ImmeubleInput) => void;
}

const ImmeublesContext = createContext<ImmeublesContextValue | null>(null);

function toOwnerImmeuble(source: PlatformImmeuble): Immeuble {
  return {
    id: source.id,
    nom: source.nom,
    description: source.description,
    photo: source.photo,
    regionId: source.regionId,
    provinceId: source.provinceId,
    communeId: source.communeId,
    secteurId: source.secteurId,
    region: source.region,
    province: source.province,
    commune: source.commune,
    secteur: source.secteur,
    adresse: source.adresse,
    niveaux: source.niveaux,
    infosComplementaires: source.infosComplementaires,
    createdAt: source.createdAt,
  };
}

/**
 * Vue « Propriétaire » du store unifié de la plateforme.
 * Les immeubles de cet espace (scope « proprietaire ») proviennent du même
 * store que ceux de l'espace Agence et du Super Admin : aucune duplication de
 * données. Le jour où la base est branchée, une simple requête filtrée sur
 * `scope = 'proprietaire'` remplacera ce filtre.
 */
export function ImmeublesProvider({ children }: { children: ReactNode }) {
  const platform = usePlatformData();

  const immeubles = useMemo(
    () => platform.immeubles.filter((item) => item.scope === "proprietaire").map(toOwnerImmeuble),
    [platform.immeubles]
  );

  const addImmeuble = useCallback(
    (input: ImmeubleInput) => {
      const created = platform.addImmeuble({ ...input, scope: "proprietaire", proprietaireId: "" });
      return toOwnerImmeuble(created);
    },
    [platform.addImmeuble]
  );

  const updateImmeuble = useCallback(
    (id: string, input: ImmeubleInput) => {
      platform.updateImmeuble(id, { ...input, scope: "proprietaire", proprietaireId: "" });
    },
    [platform.updateImmeuble]
  );

  const removeImmeuble = useCallback(
    (id: string) => {
      platform.removeImmeuble(id);
    },
    [platform.removeImmeuble]
  );

  const getImmeuble = useCallback((id: string) => immeubles.find((item) => item.id === id), [immeubles]);

  const value = useMemo(
    () => ({ immeubles, addImmeuble, getImmeuble, removeImmeuble, updateImmeuble }),
    [immeubles, addImmeuble, getImmeuble, removeImmeuble, updateImmeuble]
  );

  return <ImmeublesContext.Provider value={value}>{children}</ImmeublesContext.Provider>;
}

export function useImmeubles(): ImmeublesContextValue {
  const context = useContext(ImmeublesContext);
  if (!context) {
    throw new Error("useImmeubles doit être utilisé à l'intérieur de ImmeublesProvider");
  }
  return context;
}